import { useCallback, useRef, useState } from 'react';
import {
  findSentCharge,
  toChargeResultParams,
  type ChargeDraft,
  type ChargeLimits,
  type SentChargeQuery,
} from '@mypoz/shared';

import {
  fetchRecentChargeRows,
  submitCharge,
  type ChargeOutcome,
} from '@/features/china-payment/api.js';
import { CHINA_PAYMENT_TYPE_RULES } from '@/features/china-payment/config.js';

/**
 * Where sending a request stands. `refused`: the vendor said no — nothing to look for. `checking`:
 * no answer came back, so the history is being read for it. `unconfirmed`: it isn't there (yet), or
 * the history could not be read either.
 */
export type SendState = 'idle' | 'sending' | 'refused' | 'checking' | 'unconfirmed';

/** A beat before the first look, so a request still being saved when the answer was lost is in. */
const FIRST_LOOK_DELAY_MS = 1500;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface SendCharge {
  readonly state: SendState;
  /** Sends the request once. Resolves when `ChargeResult` has answered (or failed to). */
  readonly send: (draft: ChargeDraft, limits: ChargeLimits) => Promise<void>;
  /** Reads the history again for the request whose answer was lost. */
  readonly checkAgain: () => Promise<void>;
  /** Forgets the last attempt, for a new request. */
  readonly reset: () => void;
}

/**
 * Sends a China Payment request (`ChargeResult`, MYP-109), once per click, and never again by
 * itself. Kept by the request card, not by step 3, so leaving the step cannot forget an attempt
 * whose answer was lost.
 *
 * When no answer comes back (network, timeout, or a reply the app cannot read) the request may or
 * may not be recorded, and sending it again could make a second one for the same transfer. So the
 * customer's history (`getalipaylist`, page 1) is read instead: the row with this type, amount and
 * receipt from this moment on, and not one already reported (`findSentCharge`), means it arrived,
 * and the customer sees it as sent. Otherwise they are told it couldn't be confirmed, with "check
 * again" and their records.
 */
export function useSendCharge(onSent: (reference?: string) => void): SendCharge {
  const [state, setState] = useState<SendState>('idle');
  const busy = useRef(false);
  const lastQuery = useRef<SentChargeQuery | undefined>(undefined);
  // Requests this page has already shown as sent: never taken for a later one.
  const reported = useRef<string[]>([]);

  const sent = useCallback(
    (reference?: string) => {
      if (reference) reported.current.push(reference);
      lastQuery.current = undefined;
      setState('idle');
      onSent(reference);
    },
    [onSent],
  );

  const lookUp = useCallback(
    async (query: SentChargeQuery) => {
      setState('checking');
      try {
        const rows = await fetchRecentChargeRows();
        const found = findSentCharge(rows, { ...query, exclude: [...reported.current] });
        if (found) {
          sent(found.reference);
          return;
        }
      } catch (cause) {
        console.error('[china-payment] could not read the history to confirm a request', cause);
      }
      setState('unconfirmed');
    },
    [sent],
  );

  const send = useCallback(
    async (draft: ChargeDraft, limits: ChargeLimits) => {
      if (busy.current || draft.rmbCents === undefined) return;
      // The screens never offer a type that is off; this makes sure nothing else can either.
      if (!CHINA_PAYMENT_TYPE_RULES[draft.type].submit) return;
      const now = new Date();
      const params = toChargeResultParams(draft, now, limits);
      if (!params) return;

      busy.current = true;
      setState('sending');
      let outcome: ChargeOutcome;
      try {
        outcome = await submitCharge(params);
      } catch (cause) {
        // The call broke instead of answering (say, the connection dropped while the reply was
        // being read): as good as no answer, so it is checked like one — never left "sending".
        console.warn('[china-payment] the request failed without an answer', cause);
        outcome = { kind: 'unconfirmed', code: 'THROWN' };
      }
      if (outcome.kind === 'submitted') {
        busy.current = false;
        sent(outcome.orderNo);
        return;
      }
      if (outcome.kind === 'refused') {
        // The code and the vendor's text only: the request itself holds the customer's ID number.
        console.error('[china-payment] the request was refused', outcome.code, outcome.message);
        busy.current = false;
        setState('refused');
        return;
      }
      console.warn('[china-payment] no answer to the request', outcome.code);
      const query: SentChargeQuery = {
        type: draft.type,
        rmbCents: draft.rmbCents,
        since: now,
        ...(draft.receiptPath ? { receiptPath: draft.receiptPath } : {}),
      };
      lastQuery.current = query;
      setState('checking');
      // Not awaited: the confirm dialog closes now, and the check shows on the page.
      void (async () => {
        try {
          await wait(FIRST_LOOK_DELAY_MS);
          await lookUp(query);
        } finally {
          busy.current = false;
        }
      })();
    },
    [lookUp, sent],
  );

  const checkAgain = useCallback(async () => {
    const query = lastQuery.current;
    if (busy.current || !query) return;
    busy.current = true;
    try {
      await lookUp(query);
    } finally {
      busy.current = false;
    }
  }, [lookUp]);

  const reset = useCallback(() => {
    if (busy.current) return;
    lastQuery.current = undefined;
    setState('idle');
  }, []);

  return { state, send, checkAgain, reset };
}
